import React from 'react';
import { Link } from 'react-router-dom';
import { Alert, Collapse, Descriptions, Space, Typography } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import SubmissionStatus from './SubmissionStatus';
import { formatDateTime, processingLabels } from '../utils/recordView';

const { Title, Paragraph, Text } = Typography;
const sourceId = value => typeof value === 'object' ? value?._id : value;
const display = value => value === true ? '是' : value === false ? '否' : value || '—';
const json = value => <pre className="technical-json">{value ? typeof value === 'string' ? value : JSON.stringify(value, null, 2) : '暂无数据'}</pre>;

export function RegistrationDetails({ record, action }) {
  const { hasPermission } = useAuth();
  const person = record.aiExtraction || {};
  const fields = [['姓名', record.name], ['手机号', record.phone], ['城市', record.city], ['站点 / 岗位', record.site],
    ['业务线', record.businessLine], ['加盟商', record.franchiseeName], ['用工类型', person.yglx || record.employmentType],
    ['车辆需求', person.che], ['住宿需求', person.zhusu], ['面试时间原文', person.interviewTimeText]];
  return <Space direction="vertical" size={24} className="full-width">
    <Alert type={record.submissionStatus === 'accepted' ? 'success' : ['unknown', 'pending'].includes(record.submissionStatus) ? 'warning' : 'info'} showIcon
      message={<SubmissionStatus status={record.submissionStatus} />} description={record.submissionMessage || '历史记录保留原有资料。'} />
    {['unknown', 'pending'].includes(record.submissionStatus) && <Text type="secondary">请先核对伙伴通是否已建档，再决定后续处理。</Text>}
    {action}
    <div><Title level={5}>报名信息</Title><Descriptions column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }} bordered size="small">
      {fields.map(([label, value]) => <Descriptions.Item key={label} label={label}>{display(value)}</Descriptions.Item>)}
    </Descriptions></div>
    {record.submissionWarnings?.map((warning, index) => <Alert key={index} type="warning" showIcon message={warning} />)}
    <div><Title level={5}>岗位与接收结果</Title><Descriptions column={1} bordered size="small">
      <Descriptions.Item label="匹配岗位">{record.jobSnapshot?.name || record.site || '—'}</Descriptions.Item>
      <Descriptions.Item label="岗位编码">{record.externalJobCode || '—'}</Descriptions.Item>
      <Descriptions.Item label="伙伴通记录编号">{record.partnerRecordId || '—'}</Descriptions.Item>
      <Descriptions.Item label="来源群 / 发送人">{record.senderGroupName || '—'} / {record.sender || '—'}</Descriptions.Item>
      <Descriptions.Item label="创建时间">{formatDateTime(record.createdAt)}</Descriptions.Item>
    </Descriptions><Paragraph type="secondary" className="detail-note">伙伴通已接收表示对方已受理、建档，后续发送由伙伴通处理。</Paragraph></div>
    <div><div className="section-heading"><Title level={5}>报名原始消息</Title>
      {sourceId(record.relatedWeChatRecord) && hasPermission('wechatRecords', 'read') && <Link to={`/wechat-records?detail=${encodeURIComponent(sourceId(record.relatedWeChatRecord))}`}>查看来源消息</Link>}
    </div><Paragraph className="original-message">{record.spoken || record.originalText || '暂无原文'}</Paragraph></div>
    {record.submissionAttempts?.length > 0 && <div><Title level={5}>人工操作记录</Title>
      {record.submissionAttempts.map(attempt => <div key={attempt.requestId} className="activity-entry">
        <Space wrap><Text strong>{attempt.operatorName || '管理员'}</Text><Text type="secondary">{formatDateTime(attempt.startedAt)}</Text><SubmissionStatus status={attempt.status} /></Space>
        <Paragraph>{attempt.message || '正在等待接收结果'}</Paragraph>
      </div>)}
    </div>}
    <Collapse items={[{ key: 'technical', label: '技术详情', children: <>
      <Paragraph>请求编号：<Text copyable={Boolean(record.submissionRequestId)}>{record.submissionRequestId || '—'}</Text></Paragraph>
      <Paragraph>HTTP 状态：{record.submissionHttpStatus || '—'}</Paragraph>
      {[['识别结果', record.aiExtraction], ['岗位匹配依据', record.matchDetails], ['提交内容', record.submissionPayload], ['接收响应', record.submissionResponse]].map(([label, value]) => <div key={label}><Title level={5}>{label}</Title>{json(value)}</div>)}
    </> }]} />
  </Space>;
}

export function MessageDetails({ record }) {
  const { hasPermission } = useAuth();
  return <Space direction="vertical" size={24} className="full-width">
    <Alert showIcon type={['failed', 'needs_review', 'unknown', 'partial'].includes(record.processingStatus) ? 'warning' : 'info'}
      message={processingLabels[record.processingStatus] || '历史消息'} description={record.processingError || '系统会自动更新处理结果。'} />
    <Descriptions column={1} bordered size="small">
      <Descriptions.Item label="来源群">{record.groupName || '—'}</Descriptions.Item>
      <Descriptions.Item label="发送人">{record.senderName || '—'}</Descriptions.Item>
      <Descriptions.Item label="接收机器人">{record.robotName || record.worktoolRobotId || '—'}</Descriptions.Item>
      <Descriptions.Item label="接收时间">{formatDateTime(record.timestamp)}</Descriptions.Item>
      {record.matchedKeywords?.length > 0 && <Descriptions.Item label="命中的 AI 提取触发词">{record.matchedKeywords.join('、')}</Descriptions.Item>}
    </Descriptions>
    <div><Title level={5}>原始消息</Title><Paragraph className="original-message">{record.content || record.spoken || '暂无文字内容'}</Paragraph></div>
    {record.processingResults?.length > 0 && <div><Title level={5}>逐人报名结果</Title>
      {record.processingResults.map((result, index) => <div key={result.pushRecordId || index} className="activity-entry">
        <Space wrap><Text strong>{result.name || '本次处理'}</Text><SubmissionStatus status={result.status} /></Space>
        {result.jobName && <Paragraph>岗位：{result.jobName}</Paragraph>}
        <Paragraph>{result.message}</Paragraph>
        {result.pushRecordId && hasPermission('pushRecords', 'read') && <Link to={`/push-records?detail=${encodeURIComponent(result.pushRecordId)}`}>查看报名详情</Link>}
        {result.recordingError && <Alert type="warning" message={result.recordingError} showIcon />}
      </div>)}
    </div>}
    {!record.processingResults?.length && sourceId(record.relatedPushRecord) && hasPermission('pushRecords', 'read') && <Link to={`/push-records?detail=${encodeURIComponent(sourceId(record.relatedPushRecord))}`}>查看关联报名</Link>}
    <Collapse items={[{ key: 'technical', label: '识别与技术详情', children: <>
      <Paragraph>消息编号：{record.messageId || '—'}</Paragraph>
      <Title level={5}>识别结果</Title>{json(record.extractionResult)}
    </> }]} />
  </Space>;
}
